import React, { useEffect, useMemo } from "react";
import { useGame, usePlayer, usePlayers } from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import {
  dollars,
  stageStartingMoney,
} from "../lossFrameMoney";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";
import { usesDisclosedAssignedDifficulty } from "../../../shared/experimentBehavior.js";

export function ExperimentIntroStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);
  const discloseAssignedDifficulty = usesDisclosedAssignedDifficulty(design);
  const stage1StartingMoney = stageStartingMoney(design, 1);
  const stage2StartingMoney = stageStartingMoney(design, 2);
  const totalStartingMoney = stage1StartingMoney + stage2StartingMoney;

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );

  useEffect(() => {
    const prePetSliderValue = player.get("prePetSliderValue");
    const prePetCatsOnRight = player.get("prePetCatsOnRight");
    const prePetCatsPreference = player.get("prePetCatsPreference");

    if (prePetSliderValue != null) {
      player.stage.set("prePetSliderValue", prePetSliderValue);
    }
    if (prePetCatsOnRight != null) {
      player.stage.set("prePetCatsOnRight", prePetCatsOnRight);
    }
    if (prePetCatsPreference != null) {
      player.stage.set("prePetCatsPreference", prePetCatsPreference);
    }
  }, [player]);

  function handleContinue() {
    player.stage.set("submit", true);
  }

  return (
    <div className="py-10 max-w-4xl mx-auto px-8 space-y-8">
      <p className="text-5xl font-medium text-gray-900">Welcome</p>


      {lossFrame ? (
        <section className="space-y-4 text-gray-900">
          <div className="text-3xl font-semibold uppercase tracking-wide text-black-600">
            Your money is at risk
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-md border-2 border-gray-900 bg-white p-5 shadow-sm">
              <div className="text-lg font-semibold uppercase tracking-wide text-gray-500">
                You have
              </div>
              <div className="mt-2 text-5xl font-semibold">
                {dollars(totalStartingMoney)}
              </div>
            </div>

            <div className="rounded-md border-2 border-gray-900 bg-white p-5 shadow-sm">
              <div className="text-lg font-semibold uppercase tracking-wide text-gray-500">
                {counterpart.capitalizedTerm} has
              </div>
              <div className="mt-2 text-5xl font-semibold">
                {dollars(totalStartingMoney)}
              </div>
            </div>
          </div>
        </section>
      ) : null}


      <div className="rounded-md border border-gray-200 p-5 space-y-3 text-2xl text-gray-700">
        
        <p>
          You're matched with <b>{counterpart.article} {counterpart.term}</b>—another real participant.
        </p>

        <p>
          You will each play <b>two</b> mouse-tracking games.
        </p>

        
          {lossFrame ? (
            <>
            <p>
              <b>Your score</b> in these games <b> prevents money from being lost</b>.
            </p>
            </>
          ) : (
            <>
            <p>
              The <b>better you do</b> in the games, the <b> more money</b> goes into a pot of money that <b>you share with your {counterpart.term}</b>.
            </p>

            <p>
              <b>How</b> the shared pot of <b>money gets divided</b> between you and your {counterpart.term} is <b>different</b> in <b>Game 1</b> and <b>Game 2</b>.
            </p>
            </>
          )}

        <div className="pl-6 space-y-3">
          <p>
            <b>Game 1:</b>{" "}
            {lossFrame ? (
              <>
                Whoever does <b>better</b> gets <u><b>the money saved by both players</b></u>.
              </>
            ) : (
              <>
                Whoever does <b>better</b> takes <u><b>all the money</b></u> from both players.
              </>
            )}
          </p>

          <p>
            <b>Game 2:</b>{" "}
            {lossFrame ? (
              <>
                You <u><b>negotiate</b></u> with your {counterpart.term} over the saved money.
              </>
            ) : (
              <>
                You <u><b>negotiate</b></u> with your {counterpart.term} how to <b>split the money</b>.
              </>
            )}
          </p>
        </div>

        <p>
          {lossFrame ? (
            <>
              You will <u><b>take home</b></u> the money you're <b>left with</b> at the end.
            </>
          ) : (
            <>
              This <b>money</b> will be <u><b>added to your actual payment</b></u>.
            </>
          )}
        </p>

        {discloseAssignedDifficulty ? (
          <p>
            <b>NOTE:</b> 
            <span className="block pl-4">Randomly, either <b>you</b> or your <b>{counterpart.term}</b> will play a <b><u>harder</u> version of Game 1</b>.</span>
          </p>
        ) : null}
        
      </div>


      <div className="rounded-md border border-gray-200 p-5 space-y-3 text-2xl text-gray-700">
        <p>
          Because you have {counterpart.article} {counterpart.term}, you will be <b>automatically moved</b> to the next page
          when the <b>timer hits zero</b> if you haven't <b>clicked "<u>Continue</u>"</b>.
        </p>

        <p>
          If this happens too often, you will be <u><b>removed without pay</b></u>.
        </p>

        <p>
          If your <b>{counterpart.term} leaves</b> or is <b>removed</b>, you will still receive <b>payment</b>.
        </p>
      </div>

      <div className="flex items-center space-x-4">
        <Button handleClick={handleContinue} autoFocus disabled={player.stage.get("submit")}>
          <p className="text-2xl">Continue</p>
        </Button>
        {player.stage.get("submit") && !allSubmitted ? (
          <span className="text-sm text-gray-500">
            Waiting for {counterpart.term}…
          </span>
        ) : null}
      </div>
    </div>
  );
}
